/**
 * Import Dependency
 */
import Vue from 'vue'
import { getBuurtInfo } from 'service/geodata'


/**
 * Import API
 */
import { getBuurtPandInfo } from 'service/geodata'

/**
 * Helper Functions
 */

/**
 * For each barcode segment, generate the possible options.
 *   If the segment is enabled, pass only the corresponding code value of the passed visible barcode
 *   If the segment is not enabled, pass all possible code values of the available filter options for this code segment
 * 
 * Example result: [[1,2,3,4,5,6,7], ['A'], [1], [1,2,3,4,5,6]]
 */
const generateSegmentOptions = function({ barcode, components }) {
  return components.map(component => {
    return (component.enabled) 
      ? [ barcode[`${component.id}_code`] ]
      : component.items.map(item => item.code)
  })
}

/**
 * Take an array of arrays and generate an array of all possible combinations
 * e.g.: [[1], ['A','B'], [1,2]] => ['1A1', '1A2', '1B1', '1B2']
 */
const convertSegmentedListToVariationsList = function({ list }) {
  let combine = function(roots, elements) {
    return roots.map(root => elements.map(element => root+element)).flat()
  }
  return list.reduce((result, elements) => {
    return (result.length === 0) ? elements : combine(result, elements)
  }, [])
}



// {
//   "bouwjaar":2008,
//   "gebruiksdoel":"woonfunctie",
//   "pand_id":402100001476539,
//   "eigendom":"Eigenaar bewoner",
//   "energielabel":"F",
//   "wozwaardeklasse":
//   "€200.000 tot €250.000",
//   "bouwjaarklasse":"Na 2006",
//   "codering":"1F46",
//   "bouwblok":123
// }

/**
 * Declare Variable
 */
const state = {
  neighborhoods: getBuurtInfo(),
  neighborhoodBuildingData: {}
}

const getters = {
  getNeighborhoodGeoData: state => ({ neighborhood }) => state.neighborhoods[neighborhood],
  getNeighborhoodCoordinates: state => ({ neighborhood }) => state.neighborhoods[neighborhood].coordinates,
  hasNeighborhoodBuildingData: state => ({ neighborhood }) => !!state.neighborhoodBuildingData[neighborhood],
  getNeighborhoodBuildingData: state => ({ neighborhood }) => state.neighborhoodBuildingData[neighborhood] || false,

  getActiveNeighborhoodGeoData: (state, getters, rootState, rootGetters) => {
    return state.neighborhoods[rootGetters['filters/getActiveNeighborhood']]
  },
  getActiveNeighborhoodCoordinates: (state, getters, rootState, rootGetters) => {
    return state.neighborhoods[rootGetters['filters/getActiveNeighborhood']].coordinates
  },

  hasActiveNeighborhoodBuildingData: (state, getters, rootState, rootGetters) => {
    return !!state.neighborhoodBuildingData[rootGetters['filters/getActiveNeighborhood']]
  },
  getActiveNeighborhoodBuildingData: (state, getters, rootState, rootGetters) => {
    return state.neighborhoodBuildingData[rootGetters['filters/getActiveNeighborhood']] || []
  },

  getActiveNeighborhoodCodedBuildingData: (state, getters, rootState, rootGetters) => {

    let components = rootGetters['filters/getActiveBarcodeComponents']
    let ids = components.map(component => component.id)

    return getters.getActiveNeighborhoodBuildingData.map(building => {
      building.codering_original = building.codering
      building.codering = ids.map(id => building[`${id}_code`]).join('')
      return building
    })
  },

  /**
   * Get the barcode properties and building count per barcode
   *  returns an object, where keys are barcodes, and values the properties and count
   */
  getPropertiesAndCountPerBarcode: (state, getters) => {
    return getters.getActiveNeighborhoodCodedBuildingData.reduce((result, building) => {
      if (building.codering !== null) {
        if (result[building.codering]) {
          result[building.codering].count++
        } else {
          result[building.codering] = Object.assign(building, {
            barcode: building.codering,
            count: 1
          })
          
          // {
          //   bouwblok: building.bouwblok,
          //   barcode: building.codering,
          //   bouwjaar: building.bouwjaar,
          //   bouwjaarklasse: building.bouwjaarklasse,
          //   eigendom: building.eigendom,
          //   energielabel: building.energielabel,
          //   wozwaardeklasse: building.wozwaardeklasse,
          //   count: 1
          // }
        }
      }

      return result
    }, {})
  },

  getBarcodePropertiesOrderedByCount: (state, getters) => {
    return Object.values(getters.getPropertiesAndCountPerBarcode).sort((a,b) => a.count < b.count? 1 : -1)
  },

  /**
   * Get a list of visible buildings
   */
  getVisibleBuildings: (state, getters, rootState, rootGetters) => {
    let buildings = getters.getActiveNeighborhoodCodedBuildingData
    
    let filterInfo = rootGetters['filters/getFilterInfo']({ mode : null })

    // Go through all filter categories
    Object.values(filterInfo)
      .filter(filter => filter.component && filter.active)
      .forEach(filter => {
        // For every disabled filter item, clear the related items from the dataset
        filter.items
          .filter(item => !item.active)
          .forEach(item => {
            if (filter.categorical) {
              // Filter the building by the filter property. Any buildings matching with inactive filter values are removed
              buildings = buildings.filter(row => row[`${filter.id}_code`] !== (item.value || item.label))
            } else {
              // Filter the building by the filter property. Any buildings matching with inactive filter values are removed
              buildings = buildings.filter(row => row[filter.id] !== (item.value || item.label))
            }
        })
    })
    return buildings
  },

  /**
   * Return an array with all unique barcodes of visible buildings
   */
  getVisibleBuildingBarcodes: (state, getters) => {
    return getters.getVisibleBuildings.reduce((result, building) => {
      if (building.codering !== null && ! result.includes(building.codering)) {
        result.push(building.codering)
      }
      return result
    }, [])
  },

  /**
   * Order visible barcodes by count
   *  Returns an array with objects, each object containing the barcode properties and count
   */
  getVisibleBuildingBarcodesOrderedByCount: (state, getters) => {
    let barcodeProperties = getters.getPropertiesAndCountPerBarcode
    return getters.getVisibleBuildingBarcodes
      .map(barcode => {
        return barcodeProperties[barcode]
      })
      .sort((a, b) => a.count < b.count ? 1 : -1)
  },

  /**
   * List for UI. Shows barcode as selected
   */
  getTopVisibleBarcodes: (state, getters) => ({ count }) => {
    return getters.getVisibleBuildingBarcodesOrderedByCount
      .slice(0, count)
  },

  /**
   * List for UI. Shows barcode as selected
   */
  getRemainingVisibleBarcodes: (state, getters) => ({ count }) => {
    return getters.getVisibleBuildingBarcodesOrderedByCount
      .slice(count)
  },

  /**
   * Generate the complete barcodes, as used in the GIS filter, based on the partial barcodes
   *  e.g. 1A3 => 1A31, 1A32, 1A33, etc.
   */
  generateTopVisibleBarcodesForGIS: (state, getters, rootState, rootGetters) => ({ count }) => {

    let topBarcodes = getters.getTopVisibleBarcodes({ count })

    /**
     * STEP 0: Get all available barcode components, 
     *   and sort them in the same order as the barcode composition used in the GIS dataset.
     */
    let components = rootGetters['filters/getAllFilterInfo']
    components = Object.values(components)
      .filter(filter => !!filter.component && filter.enabled)
      .sort((a, b) => a.maporder < b.maporder ? -1 : 1)
      .map(filter => filter.categorical ? `${filter.id}_code` : filter.id)

    return topBarcodes.map(barcode => {
      let spec = components.reduce(( spec, id) => {
        spec[id] = barcode[id]
        return spec
      }, {})
      return spec
    })
  },

  /**
   * Generate the complete barcodes, as used in the GIS filter, based on the partial barcodes
   *  e.g. 1A3 => 1A31, 1A32, 1A33, etc.
   */
  generateRemainingVisibleBarcodesForGIS: (state, getters, rootState, rootGetters) => ({ count }) => {

    let topBarcodes = getters.getRemainingVisibleBarcodes({ count })

    /**
     * STEP 0: Get all available barcode components, 
     *   and sort them in the same order as the barcode composition used in the GIS dataset.
     */
    let components = rootGetters['filters/getAllFilterInfo']
    components = Object.values(components)
      .filter(filter => !!filter.component && filter.enabled)
      .sort((a, b) => a.maporder < b.maporder ? -1 : 1)
      .map(filter => filter.categorical ? `${filter.id}_code` : filter.id)

    return topBarcodes.reduce((spec, barcode) => {      
      components.forEach(( id) => {
        if (!spec[id]) {
          spec[id] = [ barcode[id] ]
          
        } else if (! spec[id].includes(barcode[id])){
          spec[id].push(barcode[id])
        }
      })
      return spec
    }, {})
  }

}
const actions = {
  
  getBuurtPandInfo({ commit, rootGetters }, { neighborhood }) {

    // Get info of all components, so that we can map the building value to the barcode component
    let components = rootGetters['filters/getAllFilterInfo']

    // Generate an object with for each component a property (by component id) containing an object of 
    // filter options, with the value as key and the filter code as value
    let componentValuesToCodes = Object.values(components)
      .filter(filter => !!filter.component && !filter.categorical)
      .reduce((result, component) => {
        result[component.id] = component.items.reduce(
          (result, item) => {
            result[item.value] = item.code
            return result
          }, {})
        return result
      }, {})
    componentValuesToCodes = Object.entries(componentValuesToCodes)
        
    getBuurtPandInfo({ municipality: process.env.VUE_APP_MUNICIPALITY, neighborhood: neighborhood.slug }).then(
      res => {
        commit('setNeighboorhoodBuildingData', { 
          // Map filter values to filter codes, and add this to the dataset
          data: res.data.result.map(
            entry => { 
              componentValuesToCodes.forEach(keyvalue => {
                let key = keyvalue[0], 
                  value = keyvalue[1];

                if (value[entry[key]] && ! entry[`${key}_code`]) {
                  entry[`${key}_code`] = value[entry[key]]
                }
              })
              return entry
            }
          ), 
          neighborhood: neighborhood.id 
        })
      }
    )
  }
}
const mutations = {
  setNeighboorhoodBuildingData(state, { data, neighborhood }) {
    Vue.set(state.neighborhoodBuildingData, neighborhood, data)
  }
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
